import * as THREE from 'three/webgpu';
import type {Viewer} from '../viewer/Viewer';

import {markCadObjectAsRuntimeDerived} from '../document/CadObjectPersistence';
import {
  cloneLinearComponentPatternPersistenceDocument,
  type LinearComponentPatternPersistenceDocument,
  LinearComponentPatternPersistenceSchemaVersion,
  parseLinearComponentPatternPersistenceDocument,
} from './LinearComponentPatternPersistence';


import {
  resolveComponentInstanceTransformRoot,
  type ResolveComponentInstanceTransformRootOptions,
} from '../assembly/component-instance';

import {
  cloneImportedModelTopologyRuntime,
  disposeImportedModelTopologyHosts,
} from '../imported-model/topology/imported-model-topology-runtime';
import {
  ImportedModelObjectMetadataService,
  rebindClonedImportedModelAssemblySelectionMetadata,
} from '../imported-model/metadata';
import {isFeatureResultMesh} from '../mesh/feature/FeatureResultMesh';
import {LinearComponentPatternController} from './LinearComponentPatternController';
import {LinearComponentPatternPreviewRenderer} from './LinearComponentPatternPreviewRenderer';
import {LinearComponentPatternInstancingAdapter} from './instancing/LinearComponentPatternInstancingAdapter';
import {LinearComponentPatternComponentDisplayNameResolver} from './LinearComponentPatternComponentDisplayNameResolver';
import {
  LINEAR_COMPONENT_PATTERN_MANAGER_ENTITY_ID,
  type LinearComponentPatternCommitData,
  type LinearComponentPatternCommittedPartRecord,
  type LinearComponentPatternCommittedRecord,
  type LinearComponentPatternDefinition,
  type LinearComponentPatternHistorySnapshot,
  type LinearComponentPatternReferenceDescriptor,
} from './LinearComponentPatternTypes';


/**
 * 线性零部件阵列 JSON 协议版本。
 */
export enum LinearComponentPatternJSONSchemaVersion {
  /** 初始版本：保存 LinearComponentPatternManager 可恢复的已提交阵列 plain data。 */
  V1 = 1,
}

/**
 * 线性零部件阵列管理器 JSON 文档。
 *
 * 设计说明：
 * 1. 该结构复用历史快照 plain data，避免 Manager 内维护第二套并行持久化模型；
 * 2. `schemaVersion` 只描述 Manager 级 JSON 协议，不代表完整项目文件协议；
 * 3. 当前不接入离线包导入导出，只供上层后续统一编排。
 */
export interface LinearComponentPatternDocumentJSON extends LinearComponentPatternHistorySnapshot {
  /** JSON 协议版本。 */
  readonly schemaVersion: LinearComponentPatternJSONSchemaVersion.V1;
}


/**
 * 线性零部件阵列管理器对象名称。
 */
enum LinearComponentPatternManagerObjectName {
  /** 管理器根对象名称。 */
  Root = '__linear_component_pattern_manager__',
  /** 阵列组名称前缀。 */
  PatternGroupPrefix = '__linear_component_pattern_group__',
}

/**
 * 线性零部件阵列管理器。
 *
 * 职责：
 * 1. 作为线性零部件阵列命令运行时的统一宿主；
 * 2. 直接持有预览渲染器与控制器，避免外部多处创建导致状态分裂；
 * 3. 为后续阵列提交结果对象、辅助 gizmo 收口提供统一 Group 根节点。
 */
export class LinearComponentPatternManager extends THREE.Group {
  /** Viewer 实例。 */
  private readonly viewer: Viewer;

  /** 线性阵列预览渲染器。 */
  public readonly previewRenderer: LinearComponentPatternPreviewRenderer;

  /** 线性零部件阵列控制器。 */
  public readonly controller: LinearComponentPatternController;

  /** 正式阵列 occurrence 到统一实例化投影的领域适配器。 */
  private readonly instancingAdapter: LinearComponentPatternInstancingAdapter;

  /** 阵列选择和正式复制体共享的组件 occurrence 显示名解析器。 */
  private readonly componentDisplayNameResolver: LinearComponentPatternComponentDisplayNameResolver;

  /** 正式复制体目标世界矩阵，提交多个实例时复用。 */
  private readonly committedTargetWorldMatrix = new THREE.Matrix4();

  /** 正式复制体世界偏移矩阵，提交多个实例时复用。 */
  private readonly committedWorldOffsetMatrix = new THREE.Matrix4();

  /** 阵列分组挂载后的目标父级世界矩阵，提交多个实例时复用。 */
  private readonly committedTargetParentWorldMatrix = new THREE.Matrix4();

  /** 正式复制体相对阵列分组的局部矩阵，提交多个实例时复用。 */
  private readonly committedTargetLocalMatrix = new THREE.Matrix4();

  /** 显式世界变换的位置分量，提交多个实例时复用。 */
  private readonly committedWorldPosition = new THREE.Vector3();

  /** 显式世界变换的旋转分量，提交多个实例时复用。 */
  private readonly committedWorldQuaternion = new THREE.Quaternion();

  /** 显式世界变换的缩放分量，提交多个实例时复用。 */
  private readonly committedWorldScale = new THREE.Vector3();

  /** 已提交阵列记录列表。 */
  private readonly committedRecords: LinearComponentPatternCommittedRecord[] = [];

  /** 提交序号计数器。 */
  private committedPatternCounter = 0;

  /**
   * 创建线性零部件阵列管理器。
   *
   * @param viewer Viewer 实例。
   */
  public constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;
    this.name = LinearComponentPatternManagerObjectName.Root;

    // Manager 及其阵列实例全部可由特征定义重建，离线模型快照不得重复保存该运行时子树。
    markCadObjectAsRuntimeDerived(this);

    this.previewRenderer = new LinearComponentPatternPreviewRenderer(viewer);
    this.controller = new LinearComponentPatternController(viewer, this.previewRenderer);
    this.instancingAdapter = new LinearComponentPatternInstancingAdapter(
      viewer,
    );
    this.componentDisplayNameResolver = new LinearComponentPatternComponentDisplayNameResolver(
      viewer.scene,
      objectId => viewer.findSceneObjectById(objectId),
    );
  }

  /**
   * 提交阵列并生成新的分组对象。
   *
   * 设计说明：
   * 1. 每次提交都创建一个新的 Group，避免不同阵列实例混入同一容器；
   * 2. 所有克隆实例都挂在该 Group 下，再由 Group 挂到管理器根上；
   * 3. 返回值用于上层判断本次提交是否成功落地。
   *
   * @param commitData 阵列提交数据。
   * @param definition
   * @param ownerAssemblyNodeId 阵列定义所属 occurrence；缺省时使用当前装配文档根。
   * @returns 提交是否成功。
   */
  public commitPattern(
    commitData: LinearComponentPatternCommitData,
    definition?: LinearComponentPatternDefinition,
    ownerAssemblyNodeId?: string,
  ): boolean {
    if (commitData.instances.length === 0) {
      return false;
    }

    this.committedPatternCounter += 1;
    const patternGroup = new THREE.Group();
    patternGroup.name = `${LinearComponentPatternManagerObjectName.PatternGroupPrefix}_${this.committedPatternCounter}`;

    const partRecords = this.populatePatternGroup(patternGroup, commitData);

    if (partRecords.length === 0) {
      this.committedPatternCounter -= 1;
      return false;
    }

    this.viewer.addObject(patternGroup, this);
    const resolvedOwnerAssemblyNodeId = ownerAssemblyNodeId?.trim()
      || this.viewer.assemblyMateController?.toJSON().assemblyId?.trim()
      || undefined;
    this.committedRecords.push({
      id: THREE.MathUtils.generateUUID(),
      ...(resolvedOwnerAssemblyNodeId ? { ownerAssemblyNodeId: resolvedOwnerAssemblyNodeId } : {}),
      groupObjectId: patternGroup.uuid,
      groupName: patternGroup.name,
      displayName: `局部线性阵列${this.committedPatternCounter}`,
      partRecords,
      instanceCount: partRecords.length,

      commitData: this.cloneCommitData(commitData),


      definition: definition ? this.cloneDefinition(definition) : undefined,

      createdAt: Date.now(),
    });

    this.syncModelDisplayModeAfterPatternChanged();

    return true;
  }

  /**
   * 按历史快照恢复线性零部件阵列结果。
   *
   * 设计说明：
   * 1. 恢复时先移除当前已提交阵列分组，再按提交数据重新克隆实例；
   * 2. 分组 ID 与实例 ID 会回写为快照中的 ID，保证设计树、Undo/Redo 与编辑特征继续引用同一节点；
   * 3. 若旧记录缺少 `commitData`，说明该记录不是可恢复主状态，直接跳过并保持错误可定位。
   *
   * @param snapshot 历史快照。
   */
  public restoreHistorySnapshot(snapshot: LinearComponentPatternHistorySnapshot): void {
    this.clearCommittedPatternGroups();
    this.committedRecords.length = 0;

    snapshot.records.forEach((record) => {
      if (!record.commitData || record.commitData.instances.length === 0) {
        return;
      }

      const patternGroup = new THREE.Group();
      patternGroup.uuid = record.groupObjectId;
      patternGroup.name = record.groupName;
      const partRecords = this.populatePatternGroup(patternGroup, record.commitData, record.partRecords);
      if (partRecords.length === 0) {
        return;
      }

      this.viewer.addObject(patternGroup, this);
      this.committedRecords.push({
        ...this.cloneCommittedRecord(record),
        partRecords,
        instanceCount: partRecords.length,
      });
    });

    this.committedPatternCounter = this.resolveNextCommittedPatternCounter();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();

    this.syncModelDisplayModeAfterPatternChanged();

  }

  /**
   * 应用阵列实例世界变换。
   *
   * 设计说明：
   * 1. 阵列偏移定义在世界空间，源组件又可能位于带旋转或缩放的嵌套装配父级下；
   * 2. 位置使用世界值、四元数与缩放使用局部值会污染正式 occurrence 的 source Mesh 世界矩阵；
   * 3. 因此先构造完整目标世界矩阵，再统一换算到阵列分组局部空间。
   *
   * @param sourceObject 源零部件对象。
   * @param clonedObject 阵列复制体对象。
   * @param instance 实例提交数据。
   * @param targetParent 复制体即将挂载的阵列分组。
   */
  private applyCommittedInstanceTransform(
    sourceObject: THREE.Object3D,
    clonedObject: THREE.Object3D,
    instance: LinearComponentPatternCommitData['instances'][number],
    targetParent: THREE.Object3D,
  ): void {
    if (instance.worldTransform) {
      this.committedWorldPosition.set(
        instance.worldTransform.position.x,
        instance.worldTransform.position.y,
        instance.worldTransform.position.z,
      );
      this.committedWorldQuaternion.set(
        instance.worldTransform.quaternion.x,
        instance.worldTransform.quaternion.y,
        instance.worldTransform.quaternion.z,
        instance.worldTransform.quaternion.w,
      );
      this.committedWorldScale.set(
        instance.worldTransform.scale.x,
        instance.worldTransform.scale.y,
        instance.worldTransform.scale.z,
      );
      this.committedTargetWorldMatrix.compose(
        this.committedWorldPosition,
        this.committedWorldQuaternion,
        this.committedWorldScale,
      );
    } else {
      sourceObject.updateWorldMatrix(true, false);
      this.committedTargetWorldMatrix.copy(sourceObject.matrixWorld);
      this.committedWorldOffsetMatrix.makeTranslation(
        instance.worldOffset.x,
        instance.worldOffset.y,
        instance.worldOffset.z,
      );
      // 世界阵列偏移左乘源世界矩阵，传播方向不受源组件自身姿态影响。
      this.committedTargetWorldMatrix.premultiply(this.committedWorldOffsetMatrix);
    }

    /** populate 阶段 targetParent 尚未挂载，按其即将成为 Manager 子级的正式层级计算世界矩阵。 */
    this.updateWorldMatrix(true, false);
    targetParent.updateMatrix();
    this.committedTargetParentWorldMatrix.multiplyMatrices(
      this.matrixWorld,
      targetParent.matrix,
    );
    this.committedTargetLocalMatrix.multiplyMatrices(
      this.committedTargetParentWorldMatrix.invert(),
      this.committedTargetWorldMatrix,
    );
    this.committedTargetLocalMatrix.decompose(
      clonedObject.position,
      clonedObject.quaternion,
      clonedObject.scale,
    );
    clonedObject.updateMatrix();
  }


  /**
   * 克隆线性阵列提交数据。
   *
   * 设计说明：
   * 历史快照会把提交数据作为 plain data 写入事务，因此这里必须深拷贝每个数值对象，
   * 避免后续编辑命令继续修改同一引用导致历史记录被污染。
   *
   * @param commitData 来源提交数据。
   * @returns 独立提交数据副本。
   */
  private cloneCommitData(commitData: LinearComponentPatternCommitData): LinearComponentPatternCommitData {
    return {
      instances: commitData.instances.map((instance) => ({
        sourceObjectId: instance.sourceObjectId,
        worldOffset: {
          x: instance.worldOffset.x,
          y: instance.worldOffset.y,
          z: instance.worldOffset.z,
        },
        ...(instance.worldTransform
          ? {
              worldTransform: {
                position: {
                  x: instance.worldTransform.position.x,
                  y: instance.worldTransform.position.y,
                  z: instance.worldTransform.position.z,
                },
                quaternion: {
                  x: instance.worldTransform.quaternion.x,
                  y: instance.worldTransform.quaternion.y,
                  z: instance.worldTransform.quaternion.z,
                  w: instance.worldTransform.quaternion.w,
                },
                scale: {
                  x: instance.worldTransform.scale.x,
                  y: instance.worldTransform.scale.y,
                  z: instance.worldTransform.scale.z,
                },
              },
            }
          : {}),
      })),
    };
  }


  /**
   * 克隆阵列定义快照。
   *
   * @param definition 来源定义。
   * @returns 独立定义副本。
   */
  private cloneDefinition(definition: LinearComponentPatternDefinition): LinearComponentPatternDefinition {
    return {
      state: {
        ...definition.state,
        direction1ReferenceDescriptor: this.cloneReferenceDescriptor(
          definition.state.direction1ReferenceDescriptor ?? null,
        ),
        direction1RotationAxisDescriptor: this.cloneReferenceDescriptor(
          definition.state.direction1RotationAxisDescriptor ?? null,
        ),
        direction1ReferenceGeometryDescriptor: this.cloneReferenceDescriptor(
          definition.state.direction1ReferenceGeometryDescriptor ?? null,
        ),
        direction1SelectedReferenceDescriptor: this.cloneReferenceDescriptor(
          definition.state.direction1SelectedReferenceDescriptor ?? null,
        ),
        direction2ReferenceDescriptor: this.cloneReferenceDescriptor(
          definition.state.direction2ReferenceDescriptor ?? null,
        ),
        direction2ReferenceGeometryDescriptor: this.cloneReferenceDescriptor(
          definition.state.direction2ReferenceGeometryDescriptor ?? null,
        ),
        direction2SelectedReferenceDescriptor: this.cloneReferenceDescriptor(
          definition.state.direction2SelectedReferenceDescriptor ?? null,
        ),
        componentObjectIds: [...definition.state.componentObjectIds],
        skippedInstanceKeys: [...definition.state.skippedInstanceKeys],
        modifiedInstanceObjectIds: [...definition.state.modifiedInstanceObjectIds],
      },
    };
  }

  /**
   * 克隆线性阵列引用描述符。
   *
   * @param descriptor 来源描述符。
   * @returns 独立描述符副本。
   */
  private cloneReferenceDescriptor(
    descriptor: LinearComponentPatternReferenceDescriptor | null,
  ): LinearComponentPatternReferenceDescriptor | null {
    if (!descriptor) {
      return null;
    }
    if ('assemblyTopologyRef' in descriptor) {
      return {
        ...descriptor,
        assemblyTopologyRef: this.cloneAssemblyTopologyRef(descriptor.assemblyTopologyRef ?? null),
      };
    }
    return { ...descriptor };
  }

  /**
   * 克隆 imported 装配拓扑引用。
   *
   * @param ref 来源引用。
   * @returns 独立引用副本。
   */
  private cloneAssemblyTopologyRef(ref: AssemblyTopologySelectionRef | null): AssemblyTopologySelectionRef | null {
    if (!ref) {
      return null;
    }
    return {
      ...ref,
      ...(ref.adjacentFaceRefs ? { adjacentFaceRefs: [...ref.adjacentFaceRefs] } : {}),
    };
  }

  /**
   * 用当前编辑结果替换已提交阵列记录。
   *
   * 设计说明：
   * 1. 编辑特征应保持原 FeatureManager 节点身份，不创建新的“局部线性阵列N”；
   * 2. 先在临时分组中生成新实例，确认成功后再替换原分组子节点，避免失败时破坏旧结果；
   * 3. 替换后同步更新记录中的定义快照和零部件记录，供下一次编辑继续恢复。
   *
   * @param groupObjectId 阵列分组对象 ID。
   * @param commitData 当前 PropertyManager 生成的提交数据。
   * @param definition 当前 PropertyManager 参数定义。
   * @returns 替换成功时返回 `true`。
   */
  public replaceCommittedRecordByGroupObjectId(
    groupObjectId: string,
    commitData: LinearComponentPatternCommitData,
    definition: LinearComponentPatternDefinition,
  ): boolean {
    const record = this.getCommittedRecordByGroupObjectId(groupObjectId);
    const targetGroup = this.viewer.findSceneObjectById(groupObjectId);
    if (!record || !(targetGroup instanceof THREE.Group) || commitData.instances.length === 0) {
      return false;
    }

    const stagingGroup = new THREE.Group();
    const nextPartRecords = this.populatePatternGroup(stagingGroup, commitData);
    if (nextPartRecords.length === 0) {
      return false;
    }

    this.releaseCommittedPatternGroupContents(targetGroup);
    while (stagingGroup.children.length > 0) {
      targetGroup.add(stagingGroup.children[0]);
    }
    this.setCommittedPatternGroupVisible(targetGroup, true);

    record.partRecords = nextPartRecords;
    record.instanceCount = nextPartRecords.length;

    record.commitData = this.cloneCommitData(commitData);

    record.definition = this.cloneDefinition(definition);
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();

    this.syncModelDisplayModeAfterPatternChanged();

    return true;
  }

  /**
   * 解析线性阵列来源零部件的稳定选择锚点。
   *
   * 设计说明：
   * 1. 线性阵列命中来源可能是 imported 零部件根，也可能是其下的可见 mesh；
   * 2. 设计树 hierarchy 使用的是 selectionObjectId 作为零部件节点主键，不能直接持久化子 mesh uuid；
   * 3. 因此提交记录必须优先落到 imported 选择锚点，再回退到 proxy/root uuid，保证 web 侧可稳定回查来源节点。
   *
   * @param sourceObject 阵列来源场景对象。
   * @returns 适合设计树回查的稳定对象 ID。
   */
  private resolvePatternSourceSelectionObjectId(sourceObject: THREE.Object3D): string {
    const hierarchySelectionObjectId = ImportedModelObjectMetadataService
      .resolveHierarchySelectionObjectId(sourceObject)
      ?.trim() ?? '';
    if (hierarchySelectionObjectId.length > 0) {
      return hierarchySelectionObjectId;
    }

    const importedSelectionProxyObjectId = ImportedModelObjectMetadataService
      .resolveSelectionProxyObjectId(sourceObject)
      ?.trim() ?? '';
    if (importedSelectionProxyObjectId.length > 0) {
      return importedSelectionProxyObjectId;
    }

    return sourceObject.uuid;
  }

  /**
   * 按阵列分组对象 ID 删除已提交阵列记录。
   *
   * 设计说明：
   * 1. 设计树中的“局部线性阵列N”节点与 `groupObjectId` 一一对应；
   * 2. 删除时需同时移除场景中的阵列分组对象与内存中的记录，避免树与场景状态不一致；
   * 3. 当前阶段删除后统一清空对象级选择，防止交互状态残留已被移除的实例对象。
   *
   * @param groupObjectId 阵列分组对象 ID。
   * @returns 删除成功时返回 `true`。
   */
  public removeCommittedRecordByGroupObjectId(groupObjectId: string): boolean {
    const normalizedGroupObjectId = groupObjectId.trim();
    if (normalizedGroupObjectId.length === 0) {
      return false;
    }
    if (!this.removeCommittedRecordByGroupObjectIdWithoutSync(normalizedGroupObjectId)) {
      return false;
    }

    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();

    this.syncModelDisplayModeAfterPatternChanged();

    return true;
  }

  /**
   * 删除单条已提交阵列，但不清空选择或刷新显示模式。
   *
   * @param groupObjectId 已规范化的阵列 Group ID。
   * @returns 找到并删除记录时返回 `true`。
   */
  private removeCommittedRecordByGroupObjectIdWithoutSync(groupObjectId: string): boolean {
    const targetRecordIndex = this.committedRecords.findIndex(
      record => record.groupObjectId === groupObjectId,
    );
    if (targetRecordIndex < 0) {
      return false;
    }
    const targetGroupObject = this.viewer.findSceneObjectById(groupObjectId);
    if (targetGroupObject) {
      this.releaseCommittedPatternGroupContents(targetGroupObject);
      targetGroupObject.removeFromParent();
    }
    this.committedRecords.splice(targetRecordIndex, 1);
    return true;
  }


  /**
   * 判断候选对象是否为当前 imported 文档的顶层装配根。
   *
   * 设计说明：
   * SolidWorks 线性零部件阵列只能复制装配中的组件，不能复制当前正在编辑的总装配文档自身。
   * imported hierarchy 的 `depth=0 + semanticKind=assembly` 正好表达当前文档根；插入的合法子装配
   * depth 大于零，因此不会被该防线误伤。
   *
   * @param object 已解析的组件 transform 根。
   * @returns 是否为当前文档的顶层装配根。
   */
  private isCurrentDocumentAssemblyRoot(object: THREE.Object3D): boolean {
    const hierarchy = ImportedModelObjectMetadataService.resolveHierarchy(object);
    return hierarchy?.depth === 0 && hierarchy.meta?.semanticKind === 'assembly';
  }

  /**
   * 输出文档根被拒绝作为阵列源的诊断信息。
   *
   * @param sourceObject 被拒绝的当前文档装配根。
   */
  private warnRejectedDocumentRootPatternSource(sourceObject: THREE.Object3D): void {
    console.warn('[LinearComponentPatternManager] 已拒绝把当前文档装配根作为线性阵列源', {
      sourceObjectId: sourceObject.uuid,
      sourceObjectName: sourceObject.name,
      assemblyNodeId: ImportedModelObjectMetadataService.resolveAssemblyNodeId(sourceObject),
    });
  }


  /**
   * 创建阵列源组件根解析选项。
   *
   * 设计说明：
   * 1. 解析规则复用组件实例模块，避免线性阵列重复理解 imported-model metadata；
   * 2. `FeatureResultMesh` 被显式拒绝，防止显示边拓扑宿主进入克隆链路；
   * 3. assemblyNodeId 回退只在入口解析时启用，场景扫描内部不再递归回退，避免同一节点重复查找。
   *
   * @param enableAssemblyNodeFallback 是否允许按 assemblyNodeId 扫描真实组件根。
   * @returns 组件根解析选项。
   */
  private createPatternSourceTransformRootResolveOptions(
    enableAssemblyNodeFallback: boolean,
  ): ResolveComponentInstanceTransformRootOptions {
    return {
      getSceneObjectById: (objectId) => this.viewer.findSceneObjectById(objectId),
      resolveSelectionProxyObjectId: (object) => (
        ImportedModelObjectMetadataService.resolveSelectionProxyObjectId(object)
      ),
      resolveHierarchySelectionObjectId: (object) => (
        ImportedModelObjectMetadataService.resolveHierarchySelectionObjectId(object)
      ),
      resolveAssemblyNodeId: (object) => (
        ImportedModelObjectMetadataService.resolveSelectionTarget(object)?.assemblyNodeId
        ?? ImportedModelObjectMetadataService.resolveAssemblyNodeId(object)
      ),
      isRejectedTransformRoot: (object) => isFeatureResultMesh(object),
      ...(enableAssemblyNodeFallback
        ? {
            findTransformRootByAssemblyNodeId: (assemblyNodeId: string) => (
              this.findPatternCloneSourceByAssemblyNodeId(assemblyNodeId)
            ),
          }
        : {}),
    };
  }

  /**
   * 按装配节点 ID 从场景中查找可克隆的真实组件根。
   *
   * 设计说明：
   * imported topology pick host 与真实组件可能共享 assemblyNodeId。解析器拒绝 pick host 后，
   * 需要在场景树里回到同一装配节点的 part / assembly 根，才能保持阵列克隆与装配语义一致。
   *
   * @param assemblyNodeId 装配节点 ID。
   * @returns 可克隆组件根；找不到时返回 `null`。
   */
  private findPatternCloneSourceByAssemblyNodeId(assemblyNodeId: string): THREE.Object3D | null {
    let matchedObject: THREE.Object3D | null = null;
    this.viewer.scene.traverse((object) => {
      if (matchedObject) {
        return;
      }

      if (ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(object) !== assemblyNodeId) {
        return;
      }

      const transformRoot = resolveComponentInstanceTransformRoot(
        object,
        this.createPatternSourceTransformRootResolveOptions(false),
      );
      if (!transformRoot || isFeatureResultMesh(transformRoot)) {
        return;
      }

      matchedObject = transformRoot;
    });
    return matchedObject;
  }

  /**
   * 输出阵列源对象被拒绝的诊断信息。
   *
   * @param sourceObject 被拒绝的原始阵列源。
   */
  private warnRejectedPatternSourceObject(sourceObject: THREE.Object3D): void {
    console.warn('[LinearComponentPatternManager] 已跳过不可克隆的导入拓扑代理阵列源', {
      sourceObjectId: sourceObject.uuid,
      sourceObjectName: sourceObject.name,
    });
  }


  /**
   * 递归恢复克隆对象材质为源对象的原始材质快照。
   *
   * 设计说明：
   * 1. 交互态会把源对象材质切为“选中材质”，直接 `clone(true)` 会继承该材质；
   * 2. 提交阶段必须读取交互状态中的原始材质快照并写回克隆对象；
   * 3. 当某个节点没有快照时保持当前材质，避免误覆盖非交互接管节点。
   *
   * @param sourceObject 源对象。
   * @param clonedObject 克隆对象。
   */
  private restoreClonedObjectOriginalMaterials(
    sourceObject: THREE.Object3D,
    clonedObjectBySourceObject: WeakMap<THREE.Object3D, THREE.Object3D>,
  ): void {
    const clonedObject = clonedObjectBySourceObject.get(sourceObject);
    if (!clonedObject) {
      return;
    }

    if (this.isMaterialAssignableObject(sourceObject) && this.isMaterialAssignableObject(clonedObject)) {
      const originalSnapshot = this.viewer.interactionStateManager
        ?.getOriginalMaterialSnapshotByObjectId(sourceObject.uuid);
      if (originalSnapshot) {
        clonedObject.material = originalSnapshot.material;
      }
    }

    this.viewer.listImportedModelSourceHierarchyChildren(sourceObject).forEach((child) => {
      this.restoreClonedObjectOriginalMaterials(child, clonedObjectBySourceObject);
    });
  }

  /**
   * 复制源实例的 imported topology runtime 注册关系到复制体。
   *
   * 设计说明：
   * 1. 特征面、线、点选择依赖 visible mesh -> body topology record -> pick host 的二阶段拾取链；
   * 2. 线性阵列复制体只是 clone 了可见对象树，并不会自动继承 WeakMap 中的 topology 仓库注册；
   * 3. 因此提交后必须把源实例的 visible mesh body 记录按克隆树镜像注册到复制体根上。
   *
   * @param sourceRoot 源实例根对象。
   * @param clonedRoot 复制体根对象。
   * @param clonedObjectBySourceObject
   */
  private cloneImportedTopologyRuntime(
    sourceRoot: THREE.Object3D,
    clonedRoot: THREE.Object3D,
    clonedObjectBySourceObject: WeakMap<THREE.Object3D, THREE.Object3D>,
  ): void {
    cloneImportedModelTopologyRuntime({
      sourceRoot,
      clonedRoot,
      resolveClonedObject: (sourceObject) => clonedObjectBySourceObject.get(sourceObject) ?? null,
      listSourceChildren: (sourceObject) => (
        this.viewer.listImportedModelSourceHierarchyChildren(sourceObject)
      ),
    });
  }

  /**
   * 判断对象是否支持材质写入。
   *
   * @param object 目标对象。
   * @returns 是否可写材质。
   */
  private isMaterialAssignableObject(
    object: THREE.Object3D,
  ): object is THREE.Mesh | THREE.Line | THREE.Sprite {
    return object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Sprite;
  }


  /**
   * 导出当前线性零部件阵列管理器业务 JSON。
   *
   * 设计说明：
   * 1. 复用历史快照 plain data，确保 JSON 与 Undo/Redo 的主状态来源一致；
   * 2. Manager ID 固定为 `LINEAR_COMPONENT_PATTERN_MANAGER_ENTITY_ID`，禁止把运行时 uuid 写入长期数据；
   * 3. 本类继承 Three.js Group，不能覆盖 Object3D.toJSON(meta)，因此业务序列化必须使用独立方法名；
   * 4. 导出前执行同一套 JSON 校验，避免不可恢复记录进入后续项目导入导出链路。
   *
   * @returns 可持久化的线性零部件阵列 JSON 文档。
   */
  public toPatternJSON(): LinearComponentPatternDocumentJSON {
    const snapshot = this.createHistorySnapshot();
    const document = this.cloneLinearComponentPatternDocumentJSON({
      schemaVersion: LinearComponentPatternJSONSchemaVersion.V1,
      managerId: LINEAR_COMPONENT_PATTERN_MANAGER_ENTITY_ID,
      records: snapshot.records,
    });

    /**
     * 线性阵列 JSON 是长期恢复入口，缺少 commitData 的旧记录无法重建实例，应在导出阶段尽早失败。
     */
    this.assertLinearComponentPatternDocumentJSON(document);
    return document;
  }

  /**
   * 从业务 JSON 恢复线性零部件阵列管理器状态。
   *
   * 设计说明：
   * 1. 先校验协议版本与记录完整性，避免无效 JSON 进入对象克隆流程；
   * 2. 复用 `restoreHistorySnapshot()` 完成旧分组清理、实例重建、计数器恢复、选择清理和渲染刷新；
   * 3. 当前 API 不接入 `.puppy` 导入导出，只提供 Manager 级恢复能力。
   *
   * @param data 外部传入的线性零部件阵列 JSON。
   */
  public fromPatternJSON(data: unknown): void {
    this.assertLinearComponentPatternDocumentJSON(data);
    const document = this.cloneLinearComponentPatternDocumentJSON(data);

    /**
     * Manager 的恢复入口只消费历史快照字段，`schemaVersion` 在校验后不再传入。
     */
    this.restoreHistorySnapshot({
      managerId: document.managerId,
      records: document.records,
    });
  }

  /**
   * 克隆线性零部件阵列 JSON 文档。
   *
   * @param document 来源 JSON 文档。
   * @returns 去除运行时引用后的 JSON 文档。
   */
  private cloneLinearComponentPatternDocumentJSON(
    document: LinearComponentPatternDocumentJSON,
  ): LinearComponentPatternDocumentJSON {
    return JSON.parse(JSON.stringify(document)) as LinearComponentPatternDocumentJSON;
  }

  /**
   * 断言未知值是线性零部件阵列 JSON 文档。
   *
   * @param data 外部输入。
   */
  private assertLinearComponentPatternDocumentJSON(
    data: unknown,
  ): asserts data is LinearComponentPatternDocumentJSON {
    if (!this.isRecord(data)) {
      throw new Error('线性零部件阵列 JSON 必须是对象。');
    }
    if (data.schemaVersion !== LinearComponentPatternJSONSchemaVersion.V1) {
      throw new Error(`不支持的线性零部件阵列 JSON 版本：${String(data.schemaVersion)}`);
    }
    if (data.managerId !== LINEAR_COMPONENT_PATTERN_MANAGER_ENTITY_ID) {
      throw new Error(`线性零部件阵列 JSON.managerId 不匹配：${String(data.managerId)}`);
    }
    if (!Array.isArray(data.records)) {
      throw new Error('线性零部件阵列 JSON.records 必须是数组。');
    }

    const recordIds = new Set<string>();
    const groupObjectIds = new Set<string>();
    const partObjectIds = new Set<string>();
    data.records.forEach((record, recordIndex) => {
      this.assertLinearComponentPatternCommittedRecord(record, recordIndex);
      if (recordIds.has(record.id)) {
        throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].id 重复：${record.id}`);
      }
      if (groupObjectIds.has(record.groupObjectId)) {
        throw new Error(
          `线性零部件阵列 JSON.records[${recordIndex}].groupObjectId 重复：${record.groupObjectId}`,
        );
      }
      recordIds.add(record.id);
      groupObjectIds.add(record.groupObjectId);

      record.partRecords.forEach((partRecord, partIndex) => {
        if (partObjectIds.has(partRecord.objectId)) {
          throw new Error(
            `线性零部件阵列 JSON.records[${recordIndex}].partRecords[${partIndex}].objectId 重复：${partRecord.objectId}`,
          );
        }
        partObjectIds.add(partRecord.objectId);
      });
    });
  }

  /**
   * 断言未知值是单条已提交线性阵列记录。
   *
   * @param value 外部输入。
   * @param recordIndex 记录下标。
   */
  private assertLinearComponentPatternCommittedRecord(
    value: unknown,
    recordIndex: number,
  ): asserts value is LinearComponentPatternCommittedRecord {
    if (!this.isRecord(value)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}] 必须是对象。`);
    }
    this.assertNonEmptyString(value.id, `线性零部件阵列 JSON.records[${recordIndex}].id`);
    this.assertNonEmptyString(
      value.groupObjectId,
      `线性零部件阵列 JSON.records[${recordIndex}].groupObjectId`,
    );
    this.assertNonEmptyString(value.groupName, `线性零部件阵列 JSON.records[${recordIndex}].groupName`);
    this.assertNonEmptyString(value.displayName, `线性零部件阵列 JSON.records[${recordIndex}].displayName`);

    if (!Array.isArray(value.partRecords)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].partRecords 必须是数组。`);
    }
    if (!Number.isInteger(value.instanceCount) || Number(value.instanceCount) < 0) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].instanceCount 必须是非负整数。`);
    }
    if (value.instanceCount !== value.partRecords.length) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].instanceCount 与 partRecords 数量不一致。`);
    }
    if (!Number.isFinite(value.createdAt)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].createdAt 必须是有限数字。`);
    }

    value.partRecords.forEach((partRecord, partIndex) => {
      this.assertLinearComponentPatternPartRecord(partRecord, recordIndex, partIndex);
    });

    if (!this.isRecord(value.commitData)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].commitData 必须是对象。`);
    }
    this.assertLinearComponentPatternCommitData(value.commitData, recordIndex, value.partRecords.length);

    if (value.definition !== undefined) {
      this.assertLinearComponentPatternDefinition(value.definition, recordIndex);
    }
  }

  /**
   * 断言未知值是阵列零部件记录。
   *
   * @param value 外部输入。
   * @param recordIndex 记录下标。
   * @param partIndex 零部件下标。
   */
  private assertLinearComponentPatternPartRecord(
    value: unknown,
    recordIndex: number,
    partIndex: number,
  ): asserts value is LinearComponentPatternCommittedPartRecord {
    if (!this.isRecord(value)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].partRecords[${partIndex}] 必须是对象。`);
    }
    this.assertNonEmptyString(
      value.objectId,
      `线性零部件阵列 JSON.records[${recordIndex}].partRecords[${partIndex}].objectId`,
    );
    this.assertNonEmptyString(
      value.sourceObjectId,
      `线性零部件阵列 JSON.records[${recordIndex}].partRecords[${partIndex}].sourceObjectId`,
    );
    this.assertNonEmptyString(
      value.displayName,
      `线性零部件阵列 JSON.records[${recordIndex}].partRecords[${partIndex}].displayName`,
    );
  }

  /**
   * 断言未知值是线性阵列提交数据。
   *
   * @param value 外部输入。
   * @param recordIndex 记录下标。
   * @param expectedInstanceCount 期望实例数量。
   */
  private assertLinearComponentPatternCommitData(
    value: unknown,
    recordIndex: number,
    expectedInstanceCount: number,
  ): asserts value is LinearComponentPatternCommitData {
    if (!this.isRecord(value)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].commitData 必须是对象。`);
    }
    if (!Array.isArray(value.instances)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].commitData.instances 必须是数组。`);
    }
    if (value.instances.length !== expectedInstanceCount) {
      throw new Error(
        `线性零部件阵列 JSON.records[${recordIndex}].commitData.instances 与 partRecords 数量不一致。`,
      );
    }
    value.instances.forEach((instance, instanceIndex) => {
      this.assertLinearComponentPatternInstanceCommitData(instance, recordIndex, instanceIndex);
    });
  }

  /**
   * 断言未知值是单个实例提交数据。
   *
   * @param value 外部输入。
   * @param recordIndex 记录下标。
   * @param instanceIndex 实例下标。
   */
  private assertLinearComponentPatternInstanceCommitData(
    value: unknown,
    recordIndex: number,
    instanceIndex: number,
  ): void {
    if (!this.isRecord(value)) {
      throw new Error(
        `线性零部件阵列 JSON.records[${recordIndex}].commitData.instances[${instanceIndex}] 必须是对象。`,
      );
    }
    this.assertNonEmptyString(
      value.sourceObjectId,
      `线性零部件阵列 JSON.records[${recordIndex}].commitData.instances[${instanceIndex}].sourceObjectId`,
    );
    this.assertVector3Record(
      value.worldOffset,
      `线性零部件阵列 JSON.records[${recordIndex}].commitData.instances[${instanceIndex}].worldOffset`,
    );

    if (value.worldTransform !== undefined) {
      this.assertWorldTransformRecord(
        value.worldTransform,
        `线性零部件阵列 JSON.records[${recordIndex}].commitData.instances[${instanceIndex}].worldTransform`,
      );
    }
  }

  /**
   * 断言未知值是线性阵列定义快照。
   *
   * @param value 外部输入。
   * @param recordIndex 记录下标。
   */
  private assertLinearComponentPatternDefinition(value: unknown, recordIndex: number): void {
    if (!this.isRecord(value)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].definition 必须是对象。`);
    }
    if (!this.isRecord(value.state)) {
      throw new Error(`线性零部件阵列 JSON.records[${recordIndex}].definition.state 必须是对象。`);
    }
  }

  /**
   * 断言未知值是三维向量记录。
   *
   * @param value 外部输入。
   * @param fieldName 字段名。
   */
  private assertVector3Record(value: unknown, fieldName: string): void {
    if (!this.isRecord(value)) {
      throw new Error(`${fieldName} 必须是对象。`);
    }
    ['x', 'y', 'z'].forEach((axis) => {
      if (!Number.isFinite(value[axis])) {
        throw new Error(`${fieldName}.${axis} 必须是有限数字。`);
      }
    });
  }

  /**
   * 断言未知值是世界变换记录。
   *
   * @param value 外部输入。
   * @param fieldName 字段名。
   */
  private assertWorldTransformRecord(value: unknown, fieldName: string): void {
    if (!this.isRecord(value)) {
      throw new Error(`${fieldName} 必须是对象。`);
    }
    this.assertVector3Record(value.position, `${fieldName}.position`);
    this.assertQuaternionRecord(value.quaternion, `${fieldName}.quaternion`);
    this.assertVector3Record(value.scale, `${fieldName}.scale`);
  }

  /**
   * 断言未知值是四元数记录。
   *
   * @param value 外部输入。
   * @param fieldName 字段名。
   */
  private assertQuaternionRecord(value: unknown, fieldName: string): void {
    if (!this.isRecord(value)) {
      throw new Error(`${fieldName} 必须是对象。`);
    }
    ['x', 'y', 'z', 'w'].forEach((axis) => {
      if (!Number.isFinite(value[axis])) {
        throw new Error(`${fieldName}.${axis} 必须是有限数字。`);
      }
    });
  }

  /**
   * 断言未知值是非空字符串。
   *
   * @param value 外部输入。
   * @param fieldName 字段名。
   */
  private assertNonEmptyString(value: unknown, fieldName: string): void {
    if (typeof value !== 'string' || value.trim().length === 0) {
      throw new Error(`${fieldName} 必须是非空字符串。`);
    }
  }

  /**
   * 判断未知值是否为普通记录对象。
   *
   * @param value 待判断值。
   * @returns 是否为普通记录对象。
   */
  private isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
  }


  /**
   * 获取已提交阵列记录。
   *
   * @returns 已提交阵列记录快照。
   */
  public getCommittedRecords(): readonly LinearComponentPatternCommittedRecord[] {
    return this.committedRecords.map((record) => this.cloneCommittedRecord(record));
  }


  /**
   * 创建只包含线性阵列特征定义的持久化文档。
   *
   * 设计说明：
   * 1. Group、克隆实例、世界变换与提交数据均属于运行时求解结果，不能进入离线包；
   * 2. 特征 ID、显示名称和定义足以在基础零部件加载完成后重新求解；
   * 3. 缺少定义的旧记录不能可靠重建，因此导出阶段直接失败，避免生成外观正确但不可编辑的离线包。
   *
   * @returns 线性阵列定义持久化文档。
   */
  public createPersistenceDocument(): LinearComponentPatternPersistenceDocument {
    const records = this.committedRecords.map((record) => {
      if (!record.definition) {
        throw new Error(`线性零部件阵列“${record.displayName}”缺少可持久化定义。`);
      }

      return {
        featureId: record.id,
        displayName: record.displayName,
        ...(record.ownerAssemblyNodeId
          ? { ownerAssemblyNodeId: record.ownerAssemblyNodeId }
          : {}),
        definition: this.cloneDefinition(record.definition),
      };
    });

    return cloneLinearComponentPatternPersistenceDocument({
      schemaVersion: LinearComponentPatternPersistenceSchemaVersion.V1,
      records,
    });
  }

  /**
   * 根据持久化定义重新求解并创建线性阵列运行时结果。
   *
   * 设计说明：
   * 1. 恢复必须发生在基础装配场景完成挂载之后，确保定义中的稳定引用可解析；
   * 2. 每条定义都通过 Controller 的正式求解入口生成提交数据，禁止恢复旧 Group 或旧实例变换；
   * 3. 任一特征恢复失败时回滚到调用前快照，避免场景出现半恢复状态。
   *
   * @param input 外部读取的线性阵列定义文档。
   * @throws 当持久化文档解析、定义引用解析、重新求解或记录恢复失败时，回滚并原样抛出异常。
   */
  public restorePersistenceDocument(input: unknown): void {
    const document = parseLinearComponentPatternPersistenceDocument(input);
    const previousSnapshot = this.createHistorySnapshot();

    try {
      this.clearCommittedPatternGroups();
      this.committedRecords.length = 0;
      this.committedPatternCounter = 0;

      document.records.forEach((record) => this.restorePersistenceRecord(record));

      this.controller.cancel();
      this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
      this.syncModelDisplayModeAfterPatternChanged();
    } catch (error) {
      // 无论失败发生在哪条定义，都清理命令态并恢复调用前完整运行时快照。
      this.controller.cancel();
      this.restoreHistorySnapshot(previousSnapshot);
      throw error;
    }
  }

  /**
   * 通过正式 Controller 求解入口恢复一条持久化阵列记录。
   *
   * @param record 已通过持久化 parser 校验的定义记录。
   * @returns 新建运行时 Group ID。
   */
  private restorePersistenceRecord(
    record: LinearComponentPatternPersistenceDocument['records'][number],
  ): string {
    if (!this.controller.restoreDefinition(record.definition)) {
      throw new Error(`无法解析线性零部件阵列“${record.displayName}”的定义引用。`);
    }

    // 只消费重新求解结果，绝不读取离线包中的实例对象或运行时变换。
    const commitData = this.controller.buildCommitData();
    const resolvedDefinition = this.controller.createDefinitionSnapshot();
    this.controller.cancel();
    if (!commitData || !this.commitPattern(
      commitData,
      resolvedDefinition,
      record.ownerAssemblyNodeId,
    )) {
      throw new Error(`无法重新求解线性零部件阵列“${record.displayName}”。`);
    }

    // `commitPattern` 创建新的运行时对象 ID；仅恢复特征级稳定标识、owner 和名称。
    const restoredRecord = this.committedRecords[this.committedRecords.length - 1];
    if (!restoredRecord) {
      throw new Error(`线性零部件阵列“${record.displayName}”提交后未生成记录。`);
    }
    restoredRecord.id = record.featureId;
    restoredRecord.displayName = record.displayName;
    return restoredRecord.groupObjectId;
  }

  /**
   * 局部加入新子树阵列，并在全部新定义成功后删除确认阶段冻结的旧阵列。
   *
   * 该入口不使用全量历史快照回滚，因为全量恢复会重建无关 Group 并改变其 Object3D 身份。
   *
   * @param input 冻结旧 Group IDs 与 Finalizer 输出的新阵列文档。
   */
  public applyScopedPersistenceDocuments(
    input: PuppyCAD.ApplyScopedLinearComponentPatternDocumentsInput,
  ): void {
    const documents = input.documents.map(document => (
      parseLinearComponentPatternPersistenceDocument(document)
    ));
    const removedGroupObjectIds = new Set(
      input.removedGroupObjectIds
        .map(groupObjectId => groupObjectId.trim())
        .filter(groupObjectId => groupObjectId.length > 0),
    );
    removedGroupObjectIds.forEach((groupObjectId) => {
      if (!this.committedRecords.some(record => record.groupObjectId === groupObjectId)) {
        throw new Error(`线性阵列局部替换计划引用未知旧 Group：${groupObjectId}。`);
      }
    });

    const retainedOwnerFeatureKeys = new Set(
      this.committedRecords
        .filter(record => !removedGroupObjectIds.has(record.groupObjectId))
        .map(record => `${record.ownerAssemblyNodeId?.trim() ?? ''}\u0000${record.id}`),
    );
    const nextOwnerFeatureKeys = new Set<string>();
    const defaultOwnerAssemblyNodeId = this.viewer.assemblyMateController?.toJSON()
      .assemblyId?.trim() ?? '';
    documents.forEach((document) => {
      document.records.forEach((record) => {
        const ownerAssemblyNodeId = record.ownerAssemblyNodeId?.trim()
          || defaultOwnerAssemblyNodeId;
        if (!ownerAssemblyNodeId) {
          throw new Error(`线性阵列局部替换记录缺少 owner：featureId=${record.featureId}。`);
        }
        const ownerFeatureKey = `${ownerAssemblyNodeId}\u0000${record.featureId}`;
        if (retainedOwnerFeatureKeys.has(ownerFeatureKey) || nextOwnerFeatureKeys.has(ownerFeatureKey)) {
          throw new Error(
            `线性阵列局部替换存在重复 owner/feature：${ownerAssemblyNodeId} / ${record.featureId}。`,
          );
        }
        nextOwnerFeatureKeys.add(ownerFeatureKey);
      });
    });

    const committedPatternCounterBeforeApply = this.committedPatternCounter;
    const createdGroupObjectIds: string[] = [];
    try {
      documents.forEach((document) => {
        document.records.forEach((record) => {
          createdGroupObjectIds.push(this.restorePersistenceRecord(record));
        });
      });
      removedGroupObjectIds.forEach((groupObjectId) => {
        this.removeCommittedRecordByGroupObjectIdWithoutSync(groupObjectId);
      });
      this.controller.cancel();
      this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
      this.syncModelDisplayModeAfterPatternChanged();
    } catch (error) {
      this.controller.cancel();
      createdGroupObjectIds.forEach((groupObjectId) => {
        this.removeCommittedRecordByGroupObjectIdWithoutSync(groupObjectId);
      });
      this.committedPatternCounter = committedPatternCounterBeforeApply;
      this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
      this.syncModelDisplayModeAfterPatternChanged();
      throw error;
    }
  }


  /**
   * 捕获线性零部件阵列历史快照。
   *
   * 设计说明：
   * 1. 快照只包含可序列化记录，不包含场景对象；
   * 2. `commitData` 负责恢复阵列实体，`definition` 负责恢复编辑特征参数；
   * 3. 上层历史事务可用该快照覆盖恢复整个线性阵列 manager。
   *
   * @returns 当前线性阵列历史快照。
   */
  public createHistorySnapshot(): LinearComponentPatternHistorySnapshot {
    return {
      managerId: LINEAR_COMPONENT_PATTERN_MANAGER_ENTITY_ID,
      records: this.committedRecords.map((record) => this.cloneCommittedRecord(record)),
    };
  }

  /**
   * 用提交数据填充阵列分组。
   *
   * 设计说明：
   * 1. 创建特征与编辑特征都要把同一份提交数据实体化为克隆零部件；
   * 2. 克隆、元数据重建和拓扑运行时复制必须保持单一实现，避免编辑链路和创建链路产生差异；
   * 3. 返回值只包含设计树需要的轻量记录，不把 Three.js 对象暴露到持久记录中。
   *
   * @param patternGroup 阵列分组对象。
   * @param commitData 阵列提交数据。
   * @returns 已创建的阵列零部件记录。
   */
  private populatePatternGroup(
    patternGroup: THREE.Group,
    commitData: LinearComponentPatternCommitData,
    expectedPartRecords: readonly LinearComponentPatternCommittedPartRecord[] = [],
  ): LinearComponentPatternCommittedPartRecord[] {
    const partRecords: LinearComponentPatternCommittedPartRecord[] = [];
    /**
     * 多源阵列必须分别从 1 开始编号；提交数据已过滤跳过实例，因此这里只统计成功创建的正式复制体。
     */
    const createdInstanceCountBySourceObjectId = new Map<string, number>();

    commitData.instances.forEach((instance, instanceIndex) => {
      const sourceObject = this.viewer.findSceneObjectById(instance.sourceObjectId);
      if (!sourceObject) {
        return;
      }

      const cloneSourceObject = this.resolveCloneablePatternSourceObject(sourceObject);
      if (!cloneSourceObject) {
        return;
      }

      const sourceDisplayName = this.componentDisplayNameResolver.resolve(cloneSourceObject);
      const sourceSelectionObjectId = this.resolvePatternSourceSelectionObjectId(cloneSourceObject);
      const sourceInstanceIndex = (createdInstanceCountBySourceObjectId.get(sourceSelectionObjectId) ?? 0) + 1;
      const expectedPartRecord = expectedPartRecords[instanceIndex];
      const clonedObjectBySourceObject = new WeakMap<THREE.Object3D, THREE.Object3D>();
      const clonedObjectIdBySourceObjectId = new Map<string, string>();
      const clonedObject = this.cloneImportedPatternSourceObject(
        cloneSourceObject,
        clonedObjectBySourceObject,
        clonedObjectIdBySourceObjectId,
      );
      if (expectedPartRecord?.objectId) {
        // 历史恢复必须保留设计树节点 ID，否则 Undo/Redo 后右键编辑会找不到原阵列实例。
        clonedObject.uuid = expectedPartRecord.objectId;
        clonedObjectIdBySourceObjectId.set(cloneSourceObject.uuid, clonedObject.uuid);
      }
      rebindClonedImportedModelAssemblySelectionMetadata({
        root: clonedObject,
        clonedRootObjectId: clonedObject.uuid,
        clonedObjectIdBySourceObjectId,
      });
      this.cloneImportedTopologyRuntime(cloneSourceObject, clonedObject, clonedObjectBySourceObject);
      this.restoreClonedObjectOriginalMaterials(cloneSourceObject, clonedObjectBySourceObject);
      this.applyCommittedInstanceTransform(
        cloneSourceObject,
        clonedObject,
        instance,
        patternGroup,
      );
      clonedObject.matrixAutoUpdate = true;
      /** 正式复制体命名属于提交职责，直接使用已解析的源名称与当前源的一基序号。 */
      const generatedDisplayName = `${sourceDisplayName}_${sourceInstanceIndex}`;
      const resolvedDisplayName = expectedPartRecord?.displayName?.trim() || generatedDisplayName;
      clonedObject.name = resolvedDisplayName;
      this.viewer.addObject(clonedObject, patternGroup);
      createdInstanceCountBySourceObjectId.set(sourceSelectionObjectId, sourceInstanceIndex);

      partRecords.push({
        objectId: clonedObject.uuid,
        sourceObjectId: sourceSelectionObjectId,
        displayName: resolvedDisplayName || `零部件${partRecords.length + 1}`,
      });
    });

    return partRecords;
  }

  /**
   * 克隆已提交线性阵列记录。
   *
   * @param record 来源记录。
   * @returns 独立记录副本。
   */
  private cloneCommittedRecord(record: LinearComponentPatternCommittedRecord): LinearComponentPatternCommittedRecord {
    const commitData = record.commitData
      ? this.cloneCommitData(record.commitData)
      : this.createCommitDataFromCommittedRecord(record);

    return {
      id: record.id,
      ...(record.ownerAssemblyNodeId
        ? { ownerAssemblyNodeId: record.ownerAssemblyNodeId }
        : {}),
      groupObjectId: record.groupObjectId,
      groupName: record.groupName,
      displayName: record.displayName,
      partRecords: record.partRecords.map((partRecord) => ({
        objectId: partRecord.objectId,
        sourceObjectId: partRecord.sourceObjectId,
        displayName: partRecord.displayName,
      })),
      instanceCount: record.instanceCount,
      ...(commitData ? { commitData } : {}),
      ...(record.definition ? { definition: this.cloneDefinition(record.definition) } : {}),
      createdAt: record.createdAt,
    };
  }

  /**
   * 从已落地阵列对象反推提交数据。
   *
   * 设计说明：
   * 旧会话中已经存在的阵列记录可能没有 `commitData` 字段。为了让这些记录在本次删除/编辑后
   * 也能参与 Undo/Redo，这里只从当前复制体对象读取世界变换，生成可恢复的 plain data。
   *
   * @param record 已提交阵列记录。
   * @returns 可用于历史恢复的提交数据；无法完整解析时返回 `null`。
   */
  private createCommitDataFromCommittedRecord(
    record: LinearComponentPatternCommittedRecord,
  ): LinearComponentPatternCommitData | null {
    const instances = record.partRecords.map((partRecord) => {
      const patternedObject = this.viewer.findSceneObjectById(partRecord.objectId);
      if (!patternedObject) {
        return null;
      }

      const worldPosition = new THREE.Vector3();
      const worldQuaternion = new THREE.Quaternion();
      const worldScale = new THREE.Vector3();
      patternedObject.getWorldPosition(worldPosition);
      patternedObject.getWorldQuaternion(worldQuaternion);
      patternedObject.getWorldScale(worldScale);

      return {
        sourceObjectId: partRecord.sourceObjectId,
        worldOffset: {
          x: 0,
          y: 0,
          z: 0,
        },
        worldTransform: {
          position: {
            x: worldPosition.x,
            y: worldPosition.y,
            z: worldPosition.z,
          },
          quaternion: {
            x: worldQuaternion.x,
            y: worldQuaternion.y,
            z: worldQuaternion.z,
            w: worldQuaternion.w,
          },
          scale: {
            x: worldScale.x,
            y: worldScale.y,
            z: worldScale.z,
          },
        },
      };
    });

    if (instances.some((instance) => instance === null)) {
      return null;
    }

    return {
      instances: instances as LinearComponentPatternCommitData['instances'],
    };
  }

  /**
   * 清理当前 manager 下的已提交阵列分组。
   *
   * @returns 无返回值。
   */
  private clearCommittedPatternGroups(): void {
    while (this.children.length > 0) {
      const patternGroup = this.children[0];
      this.releaseCommittedPatternGroupContents(patternGroup);
      this.remove(patternGroup);
    }
  }

  /**
   * 释放单个已提交阵列分组拥有的运行时资源并移除其复制体子级。
   *
   * 阵列可见几何仍共享源零部件资源，不能在这里释放；只有复制体独立注册的 imported
   * topology repository 与惰性 pick host 属于阵列分组，必须在层级清空前统一销毁。
   *
   * @param patternGroup 待清理的阵列分组。
   * @returns 无返回值。
   */
  private releaseCommittedPatternGroupContents(patternGroup: THREE.Object3D): void {
    this.instancingAdapter.releaseGroup(patternGroup.uuid);
    disposeImportedModelTopologyHosts(patternGroup);
    patternGroup.clear();
  }

  /**
   * 根据现有记录恢复提交计数器。
   *
   * @returns 下次创建阵列前应使用的计数器值。
   */
  private resolveNextCommittedPatternCounter(): number {
    return this.committedRecords.reduce((maxCounter, record) => {
      const suffix = record.groupName.replace(`${LinearComponentPatternManagerObjectName.PatternGroupPrefix}_`, '');
      const counter = Number.parseInt(suffix, 10);
      return Number.isFinite(counter) ? Math.max(maxCounter, counter) : maxCounter;
    }, 0);
  }


  /**
   * 按阵列分组对象 ID 查找已提交阵列记录。
   *
   * @param groupObjectId 阵列分组对象 ID。
   * @returns 已提交记录；未找到时返回 `null`。
   */
  public getCommittedRecordByGroupObjectId(groupObjectId: string): LinearComponentPatternCommittedRecord | null {
    const normalizedGroupObjectId = groupObjectId.trim();
    if (normalizedGroupObjectId.length === 0) {
      return null;
    }
    return this.committedRecords.find((record) => record.groupObjectId === normalizedGroupObjectId) ?? null;
  }

  /**
   * 从已提交阵列记录打开编辑会话。
   *
   * @param groupObjectId 阵列分组对象 ID。
   * @returns 成功恢复编辑状态时返回 `true`。
   */
  public beginEditCommittedRecordByGroupObjectId(groupObjectId: string): boolean {
    const record = this.getCommittedRecordByGroupObjectId(groupObjectId);
    const targetGroup = this.viewer.findSceneObjectById(groupObjectId);
    if (!record?.definition) {
      return false;
    }
    if (!(targetGroup instanceof THREE.Group)) {
      return false;
    }

    const restored = this.controller.restoreDefinition(record.definition);
    if (!restored) {
      return false;
    }

    this.setCommittedPatternGroupVisible(targetGroup, false);
    this.instancingAdapter.synchronize(this.committedRecords);
    this.viewer.requestRender?.();
    return true;
  }

  /**
   * 取消已提交阵列记录的编辑会话。
   *
   * 设计说明：
   * 1. 编辑特征期间会隐藏原阵列结果，避免旧实例与新预览重叠；
   * 2. 用户取消时必须恢复原阵列可见性，保持取消等于不修改模型；
   * 3. 本方法只处理已提交结果对象可见性，命令态清理由控制器统一完成。
   *
   * @param groupObjectId 阵列分组对象 ID。
   * @returns 成功恢复可见性时返回 `true`。
   */
  public cancelEditCommittedRecordByGroupObjectId(groupObjectId: string): boolean {
    const targetGroup = this.viewer.findSceneObjectById(groupObjectId);
    if (!(targetGroup instanceof THREE.Group)) {
      return false;
    }

    this.setCommittedPatternGroupVisible(targetGroup, true);
    this.instancingAdapter.synchronize(this.committedRecords);
    this.viewer.requestRender?.();
    return true;
  }

  /**
   * 克隆线性阵列提交所需的 imported 对象树，并跳过 FeatureResultMesh 代理子树。
   *
   * 设计说明：
   * 1. 直接 `clone(false)` 会把 imported 可见 mesh 一起裁掉，导致复制体失去选择代理与 topology 注册落点；
   * 2. 直接 `clone(true)` 又会把 `FeatureResultMesh` 二阶段代理整棵复制过去，重新引入不该落地的 featureMesh 子树；
   * 3. 因此这里采用“浅克隆当前节点 + 手动递归保留非 FeatureResultMesh 子节点”的方式，
   *    只保留 imported 正式可见树，继续排除运行时拓扑代理节点。
   *
   * @param sourceObject 源零部件对象。
   * @returns 可用于正式提交的复制体对象树。
   */
  private cloneImportedPatternSourceObject(
    sourceObject: THREE.Object3D,
    clonedObjectBySourceObject: WeakMap<THREE.Object3D, THREE.Object3D>,
    clonedObjectIdBySourceObjectId: Map<string, string>,
  ): THREE.Object3D {
    const clonedObject = sourceObject.clone(false);
    /**
     * 装配体 InstancedMesh 会把已脱离场景树的 source Mesh 设为
     * `matrixWorldAutoUpdate=false`，由抑制控制器维护其等价世界矩阵。正式阵列克隆已经进入
     * 独立业务父链，必须恢复 Three.js 的世界矩阵传播，禁止继续消费 source 的旧缓存。
     */
    clonedObject.matrixWorldAutoUpdate = true;
    clonedObject.matrixWorldNeedsUpdate = true;
    clonedObjectBySourceObject.set(sourceObject, clonedObject);
    clonedObjectIdBySourceObjectId.set(sourceObject.uuid, clonedObject.uuid);
    ImportedModelObjectMetadataService.copyObjectMetadata(sourceObject, clonedObject);

    // 活动 Unit 导致的 source Mesh 隐藏只是渲染抑制；正式复制体必须先恢复业务可见性再参与重分桶。
    if (
      sourceObject instanceof THREE.Mesh
      && clonedObject instanceof THREE.Mesh
      && this.viewer.isImportedModelSourceMeshInstanced(sourceObject)
    ) {
      clonedObject.visible = true;
    }

    // 提交阶段直接在克隆节点上回写原始材质，避免克隆体短暂继承命令态选中材质。
    if (this.isMaterialAssignableObject(sourceObject) && this.isMaterialAssignableObject(clonedObject)) {
      const originalSnapshot = this.viewer.interactionStateManager
        ?.getOriginalMaterialSnapshotByObjectId(sourceObject.uuid);
      if (originalSnapshot) {
        clonedObject.material = originalSnapshot.material;
      }
    }


    this.viewer.listImportedModelSourceHierarchyChildren(sourceObject).forEach((child) => {
      if (isFeatureResultMesh(child)) {
        return;
      }

      const clonedChild = this.cloneImportedPatternSourceObject(
        child,
        clonedObjectBySourceObject,
        clonedObjectIdBySourceObjectId,
      );
      clonedObject.add(clonedChild);
    });

    return clonedObject;
  }

  /**
   * 设置已提交阵列分组可见性。
   *
   * @param group 阵列分组对象。
   * @param visible 是否可见。
   * @returns 无返回值。
   */
  private setCommittedPatternGroupVisible(group: THREE.Group, visible: boolean): void {
    if (group.visible === visible) {
      return;
    }
    group.visible = visible;
    // stencil 代理只收集当前可见正式网格，编辑开始/取消时必须同步重建，避免残留旧阵列盖面。
    this.viewer.notifySectionableModelContentChanged();
  }


  /**
   * 在线性阵列结果变更后同步当前显示模型模式。
   *
   * 设计说明：
   * 1. 线性阵列会克隆 imported 可见对象树，并镜像 topology runtime；
   * 2. 实例投影必须先发布 Unit 并脱离 source Mesh，使显示模式控制器把默认边线直接路由到
   *    共享 instanceMatrix 的实例 LineSegments，不为实例 occurrence 创建普通显示边；
   * 3. 非实例化或不满足边线等价条件的对象继续由既有 topology host 安全降级；
   * 4. 统一通过 Viewer 门面同步，避免阵列管理器直接依赖渲染控制器实现细节。
   *
   * @returns 无返回值。
   */
  private syncModelDisplayModeAfterPatternChanged(): void {
    this.instancingAdapter.synchronize(this.committedRecords);
    this.viewer.syncModelDisplayMode();
    // 阵列新增、删除、恢复或编辑替换都会改变正式模型子树，统一在操作收口点刷新剖切包围盒与 stencil。
    this.viewer.notifySectionableModelContentChanged();
    this.viewer.requestRender?.();
  }

  /**
   * 解析线性阵列可安全克隆的源零部件对象。
   *
   * 设计说明：
   * 1. 导入模型显示边会创建 `FeatureResultMesh` 拓扑拾取宿主，它只用于二阶段拓扑命中；
   * 2. Three.js 克隆 `FeatureResultMesh` 时会调用无参构造，但该类必须带几何输入创建，因此不能作为阵列源；
   * 3. 提交层必须把拓扑代理归一到真实组件根，作为选择层之外的最终防护。
   * 4. 当前文档的顶层装配根不是装配内组件，必须拒绝，避免递归克隆完整 imported 场景树。
   *
   * @param sourceObject 控制器保存的阵列源对象。
   * @returns 可克隆组件根；无法安全解析时返回 `null`。
   */
  private resolveCloneablePatternSourceObject(sourceObject: THREE.Object3D): THREE.Object3D | null {
    const resolvedObject = resolveComponentInstanceTransformRoot(
      sourceObject,
      this.createPatternSourceTransformRootResolveOptions(true),
    ) ?? sourceObject;


    if (this.isCurrentDocumentAssemblyRoot(resolvedObject)) {
      this.warnRejectedDocumentRootPatternSource(resolvedObject);
      return null;
    }


    if (isFeatureResultMesh(resolvedObject)) {
      this.warnRejectedPatternSourceObject(sourceObject);
      return null;
    }

    return resolvedObject;
  }


  /**
   * 解析当前对象参与的线性阵列联动对象组。
   *
   * 设计说明：
   * 1. 拖拽命中的对象既可能是阵列源零部件，也可能是某个阵列实例；
   * 2. 当命中源零部件时，只联动该源零部件派生出的全部阵列实例；
   * 3. 当命中阵列实例时，只联动该实例对应的来源零部件与同源实例；
   * 4. 若同一阵列特征包含多个源零部件，拖动其中一个源链不应带动其它源链；
   * 5. 若当前对象不属于任何线性阵列关系，则回退为仅返回自身，保持现有拖拽语义不变。
   *
   * @param targetObject 当前拖拽目标对象。
   * @returns 本次应一起变换的对象组，至少包含目标对象自身。
   */
  public resolveTransformLinkedObjects(targetObject: THREE.Object3D): THREE.Object3D[] {
    return this.resolveTransformLinkContext(targetObject).linkedObjects;
  }


  /**
   * 解析当前对象参与的线性阵列联动上下文。
   *
   * 设计说明：
   * 1. A1 是线性阵列的源零部件，A2/A3 是同源复制体；
   * 2. 用户旋转 A1、A2 或 A3 中任意一个时，都必须带动同源链路内所有对象；
   * 3. 自由旋转时共同旋转中心必须来自 A1，避免每个实例各自绕自身中心旋转破坏阵列关系；
   * 4. 返回结构保持为 plain 对象，避免 pattern 模块反向依赖 move-component 的类型实现。
   *
   * @param targetObject 当前拖拽或数值旋转目标对象。
   * @returns 联动对象集合与旋转锚点。
   */
  public resolveTransformLinkContext(targetObject: THREE.Object3D): {
    linkedObjects: THREE.Object3D[];
    rotationAnchorObject: THREE.Object3D | null;
  } {
    const targetSourceObjectIds = this.collectPatternSourceTreeIdentityObjectIds(targetObject);
    const linkedSourceObjectIdsByRecord = this.resolveLinkedSourceObjectIdsByRecord(targetObject, targetSourceObjectIds);
    if (linkedSourceObjectIdsByRecord.size === 0) {
      return {
        linkedObjects: [targetObject],
        rotationAnchorObject: null,
      };
    }

    const linkedObjects = new Map<string, THREE.Object3D>();
    linkedObjects.set(targetObject.uuid, targetObject);
    let rotationAnchorObject: THREE.Object3D | null = null;

    linkedSourceObjectIdsByRecord.forEach((linkedSourceObjectIds, record) => {
      linkedSourceObjectIds.forEach((sourceObjectId) => {
        const sourceObject = this.resolvePatternSourceObjectByStableId(sourceObjectId)
          ?? (targetSourceObjectIds.has(sourceObjectId) ? targetObject : null);
        if (!rotationAnchorObject && sourceObject) {
          rotationAnchorObject = sourceObject;
        }
      });

      record.partRecords.forEach((partRecord) => {
        if (!linkedSourceObjectIds.has(partRecord.sourceObjectId)) {
          return;
        }

        const sourceObject = this.resolvePatternSourceObjectByStableId(partRecord.sourceObjectId);
        if (sourceObject) {
          const sourceTransformObject = this.resolvePatternSourceTransformObject(sourceObject);
          linkedObjects.set(sourceTransformObject.uuid, sourceTransformObject);
        }

        const patternedObject = this.viewer.findSceneObjectById(partRecord.objectId);
        if (patternedObject) {
          linkedObjects.set(patternedObject.uuid, patternedObject);
        }
      });
    });

    return {
      linkedObjects: [...linkedObjects.values()],
      rotationAnchorObject,
    };
  }


  /**
   * 按阵列记录解析当前拖拽对象所属的阵列源零部件 ID 集合。
   *
   * 设计说明：
   * 1. 直接拖拽源零部件时，当前对象的稳定选择锚点就是源 ID；
   * 2. 直接拖拽阵列复制体时，必须从命中的复制体记录反查其 sourceObjectId；
   * 3. 返回值按阵列记录分组，避免拖拽某个阵列实例时误带动其它独立阵列记录中的同源实例；
   * 4. 拖拽源零部件时可以覆盖多个阵列记录，因为源零部件移动后它派生出的实例都需要保持相对关系。
   *
   * @param targetObject 当前拖拽目标对象。
   * @param targetSourceObjectIds 当前拖拽目标可用于匹配源零部件的稳定 ID 集合。
   * @returns 阵列记录到来源零部件 ID 集合的映射。
   */
  private resolveLinkedSourceObjectIdsByRecord(
    targetObject: THREE.Object3D,
    targetSourceObjectIds: ReadonlySet<string>,
  ): Map<LinearComponentPatternCommittedRecord, Set<string>> {
    const sourceObjectIdsByRecord = new Map<LinearComponentPatternCommittedRecord, Set<string>>();

    this.committedRecords.forEach((record) => {
      record.partRecords.forEach((partRecord) => {
        if (partRecord.objectId === targetObject.uuid) {
          this.appendLinkedSourceObjectId(sourceObjectIdsByRecord, record, partRecord.sourceObjectId);
          return;
        }

        if (this.isPatternSourceRecordMatchingTarget(partRecord, targetObject, targetSourceObjectIds)) {
          this.appendLinkedSourceObjectId(sourceObjectIdsByRecord, record, partRecord.sourceObjectId);
        }
      });
    });

    return sourceObjectIdsByRecord;
  }


  /**
   * 收集线性阵列源零部件可用于匹配的稳定 ID。
   *
   * 设计说明：
   * 1. 真实 A1 命中对象可能是外层 transform root，而阵列记录保存的是 hierarchy selectionObjectId；
   * 2. imported-model 还可能同时暴露 selection proxy、assemblyNodeId 和运行时 uuid；
   * 3. 这里把这些身份统一收口，避免 A1 作为源旋转时无法反查到同源 A2/A3。
   *
   * @param sourceObject 源零部件候选对象。
   * @returns 可参与来源匹配的稳定 ID 集合。
   */
  private collectPatternSourceIdentityObjectIds(sourceObject: THREE.Object3D): Set<string> {
    const objectIds = new Set<string>();
    this.appendNonEmptyPatternSourceIdentity(objectIds, sourceObject.uuid);
    this.appendNonEmptyPatternSourceIdentity(objectIds, sourceObject.name);
    this.appendNonEmptyPatternSourceIdentity(objectIds, this.resolvePatternSourceSelectionObjectId(sourceObject));
    this.appendNonEmptyPatternSourceIdentity(
      objectIds,
      ImportedModelObjectMetadataService.resolveHierarchySelectionObjectId(sourceObject),
    );
    this.appendNonEmptyPatternSourceIdentity(
      objectIds,
      ImportedModelObjectMetadataService.resolveSelectionProxyObjectId(sourceObject),
    );
    this.appendNonEmptyPatternSourceIdentity(
      objectIds,
      ImportedModelObjectMetadataService.resolveSelectionTarget(sourceObject)?.assemblyNodeId,
    );
    this.appendNonEmptyPatternSourceIdentity(
      objectIds,
      ImportedModelObjectMetadataService.resolveSelectionTargetFromSelfOrAncestors(sourceObject)?.assemblyNodeId,
    );
    this.appendNonEmptyPatternSourceIdentity(
      objectIds,
      ImportedModelObjectMetadataService.resolveAssemblyNodeId(sourceObject),
    );
    this.appendNonEmptyPatternSourceIdentity(
      objectIds,
      ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(sourceObject),
    );
    return objectIds;
  }

  /**
   * 收集线性阵列源零部件对象树可用于匹配的稳定 ID。
   *
   * 设计说明：
   * 1. A1 在旋转入口中可能是外层 transform root，而阵列记录保存的是内部选择锚点；
   * 2. 只读取外层对象自身会漏掉内部 source selectionObjectId，导致 A1 旋转退化为单对象旋转；
   * 3. 因此匹配触发对象时向下读取整棵子树身份，但记录源对象自身仍使用单对象身份，避免不同源链路互相污染。
   *
   * @param sourceObject 源零部件候选根对象。
   * @returns 当前对象及其子树的稳定 ID 集合。
   */
  private collectPatternSourceTreeIdentityObjectIds(sourceObject: THREE.Object3D): Set<string> {
    const objectIds = new Set<string>();

    sourceObject.traverse((object) => {
      this.collectPatternSourceIdentityObjectIds(object).forEach((objectId) => {
        objectIds.add(objectId);
      });
    });

    return objectIds;
  }

  /**
   * 判断阵列源记录是否匹配当前触发对象。
   *
   * @param partRecord 已提交阵列零部件记录。
   * @param targetObject 当前拖拽或数值旋转目标对象。
   * @param targetSourceObjectIds 当前目标对象的稳定身份集合。
   * @returns 匹配同一个阵列源时返回 `true`。
   */
  private isPatternSourceRecordMatchingTarget(
    partRecord: LinearComponentPatternCommittedPartRecord,
    targetObject: THREE.Object3D,
    targetSourceObjectIds: ReadonlySet<string>,
  ): boolean {
    if (targetSourceObjectIds.has(partRecord.sourceObjectId)) {
      return true;
    }

    const recordSourceObject = this.resolvePatternSourceObjectByStableId(partRecord.sourceObjectId);
    if (!recordSourceObject) {
      return false;
    }

    if (this.isPatternSourceObjectRelatedToTarget(recordSourceObject, targetObject)) {
      return true;
    }

    const recordSourceTransformObject = this.resolvePatternSourceTransformObject(recordSourceObject);
    if (this.isPatternSourceObjectRelatedToTarget(recordSourceTransformObject, targetObject)) {
      return true;
    }

    const recordSourceObjectIds = this.collectPatternSourceIdentityObjectIds(recordSourceObject);
    return [...recordSourceObjectIds].some((objectId) => targetSourceObjectIds.has(objectId));
  }

  /**
   * 追加非空来源身份 ID。
   *
   * @param targetSet 目标 ID 集合。
   * @param value 待追加值。
   */
  private appendNonEmptyPatternSourceIdentity(targetSet: Set<string>, value: string | null | undefined): void {
    const normalizedValue = value?.trim() ?? '';
    if (normalizedValue.length === 0) {
      return;
    }

    targetSet.add(normalizedValue);
  }

  /**
   * 按稳定 ID 解析线性阵列来源对象。
   *
   * @param sourceObjectId 已提交阵列记录中的来源 ID。
   * @returns 场景中的来源对象；无法解析时返回 `null`。
   */
  private resolvePatternSourceObjectByStableId(sourceObjectId: string): THREE.Object3D | null {
    const normalizedSourceObjectId = sourceObjectId.trim();
    if (normalizedSourceObjectId.length === 0) {
      return null;
    }

    const directObject = this.viewer.findSceneObjectById(normalizedSourceObjectId);
    if (directObject) {
      return directObject;
    }

    const assemblyNodeRoot = this.findPatternCloneSourceByAssemblyNodeId(normalizedSourceObjectId);
    if (assemblyNodeRoot) {
      return assemblyNodeRoot;
    }

    let matchedObject: THREE.Object3D | null = null;
    this.viewer.scene.traverse((object) => {
      if (matchedObject) {
        return;
      }

      if (this.collectPatternSourceIdentityObjectIds(object).has(normalizedSourceObjectId)) {
        matchedObject = object;
      }
    });

    return matchedObject;
  }

  /**
   * 将线性阵列来源对象提升为真正可写的 transform 对象。
   *
   * 设计说明：
   * 1. 阵列记录可能保存内部 selectionObjectId，而组件位姿应写入外层 transform root；
   * 2. A2/A3 触发旋转时必须带动 A1 的可写根，否则只旋转内部子节点会造成视图与历史导出不一致；
   * 3. 解析失败时回退来源对象本身，保持普通 Object3D 测试与非 imported 场景兼容。
   *
   * @param sourceObject 阵列记录解析出的来源对象。
   * @returns 可写 transform 对象。
   */
  private resolvePatternSourceTransformObject(sourceObject: THREE.Object3D): THREE.Object3D {
    return resolveComponentInstanceTransformRoot(
      sourceObject,
      this.createPatternSourceTransformRootResolveOptions(true),
    ) ?? sourceObject;
  }

  /**
   * 判断阵列来源对象与当前触发对象是否处在同一 transform 作用域。
   *
   * @param sourceObject 阵列来源对象或其 transform 根。
   * @param targetObject 当前拖拽或旋转目标对象。
   * @returns 两者相同或存在父子包含关系时返回 `true`。
   */
  private isPatternSourceObjectRelatedToTarget(
    sourceObject: THREE.Object3D,
    targetObject: THREE.Object3D,
  ): boolean {
    return sourceObject === targetObject
      || this.isObjectDescendantOf(sourceObject, targetObject)
      || this.isObjectDescendantOf(targetObject, sourceObject);
  }

  /**
   * 判断对象是否位于指定祖先对象子树内。
   *
   * @param object 待判断对象。
   * @param ancestor 候选祖先对象。
   * @returns 位于祖先对象子树内时返回 `true`。
   */
  private isObjectDescendantOf(object: THREE.Object3D, ancestor: THREE.Object3D): boolean {
    let current = object.parent;
    while (current) {
      if (current === ancestor) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }


  /**
   * 向指定阵列记录追加需要联动的来源零部件 ID。
   *
   * @param targetMap 阵列记录到来源零部件 ID 集合的映射。
   * @param record 当前阵列记录。
   * @param sourceObjectId 来源零部件对象 ID。
   */
  private appendLinkedSourceObjectId(
    targetMap: Map<LinearComponentPatternCommittedRecord, Set<string>>,
    record: LinearComponentPatternCommittedRecord,
    sourceObjectId: string,
  ): void {
    const currentSourceObjectIds = targetMap.get(record) ?? new Set<string>();
    currentSourceObjectIds.add(sourceObjectId);
    targetMap.set(record, currentSourceObjectIds);
  }

  /**
   * 增量更新阵列特征组独立拥有的实例矩阵。
   *
   * @param changedObjects 本轮发生变换的业务对象。
   * @returns 实际更新的阵列实例槽数量。
   */
  public updateInstancedTransforms(changedObjects: readonly THREE.Object3D[]): number {
    return this.instancingAdapter.updateTransforms(changedObjects);
  }


  /**
   * 释放管理器资源。
   *
   * @returns 无返回值。
   */
  public dispose(): void {
    this.controller.dispose();
    this.instancingAdapter.dispose();
    this.committedRecords.length = 0;
    this.committedPatternCounter = 0;
    this.clearCommittedPatternGroups();
  }
}
